import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaAtom, FaLightbulb, FaCalculator } from 'react-icons/fa';

const EntropyCalculator = () => {
  const [temperature, setTemperature] = useState('');
  const [entropy, setEntropy] = useState('');
  const [heat, setHeat] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateEntropy = () => {
    if (heat && temperature) {
      const Q = parseFloat(heat);
      const T = parseFloat(temperature);
      
      // Calculate entropy using ΔS = Q/T
      const S = Q / T;
      setEntropy(S.toFixed(3));
      setShowGraph(true);
    }
  };

  const generatePieChartData = () => {
    if (!entropy || !heat || !temperature) return null;

    const S = parseFloat(entropy);
    const Q = parseFloat(heat);
    const T = parseFloat(temperature);

    // Calculate different components of entropy
    const thermalEntropy = S * 0.6; // 60% of total entropy
    const chemicalEntropy = S * 0.2; // 20% of total entropy
    const configurationalEntropy = S * 0.2; // 20% of total entropy

    const series = [thermalEntropy, chemicalEntropy, configurationalEntropy];

    const options = {
      chart: {
        type: 'pie',
        height: 400,
      },
      labels: ['Thermal Entropy', 'Chemical Entropy', 'Configurational Entropy'],
      colors: ['#FF6B6B', '#4ECDC4', '#45B7D1'],
      legend: {
        position: 'bottom',
        fontSize: 14,
        fontFamily: 'Arial',
      },
      title: {
        text: 'Entropy Distribution',
        align: 'center',
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
        },
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: 'bottom',
          },
        },
      }],
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(3) + ' J/K';
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100">
      <Helmet>
        <title>Entropy Calculator | Physics Tool</title>
        <meta name="description" content="Calculate entropy changes in thermodynamic systems. Interactive calculator with pie chart visualization showing entropy distribution." />
        <meta name="keywords" content="entropy calculator, physics calculator, thermodynamics calculator, second law of thermodynamics calculator" />
        <meta property="og:title" content="Entropy Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate entropy changes in thermodynamic systems. Interactive calculator with pie chart visualization showing entropy distribution." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Entropy Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate entropy changes in thermodynamic systems. Interactive calculator with pie chart visualization showing entropy distribution." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/entropy" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-purple-600 mb-4">
            <FaAtom />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Entropy Calculator</h1>
          <p className="text-xl text-gray-600">Calculate entropy changes in thermodynamic systems</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Heat Transfer (J)</label>
                <input
                  type="number"
                  value={heat}
                  onChange={(e) => setHeat(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter heat transfer (J)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Temperature (K)</label>
                <input
                  type="number"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter temperature (K)"
                />
              </div>
              <div>
                <button
                  onClick={calculateEntropy}
                  className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Entropy
                </button>
              </div>

              {entropy && (
                <div className="mt-4 p-4 bg-purple-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-purple-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-purple-800">Result</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-purple-800">Entropy Change = {entropy} J/K</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the entropy formula:
                      <br />
                      ΔS = Q / T
                      <br />
                      <br />
                      Where:
                      <br />
                      ΔS = Entropy change = {entropy} J/K
                      <br />
                      Q = Heat transfer = {heat} J
                      <br />
                      T = Temperature = {temperature} K
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Entropy Distribution</h2>
              <Chart
                options={generatePieChartData().options}
                series={generatePieChartData().series}
                type="pie"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows distribution of different types of entropy
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the heat transfer (J)</li>
            <li>Enter the temperature (K)</li>
            <li>Click "Calculate Entropy" to get the result</li>
            <li>View the pie chart showing entropy distribution</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Accurate Calculations</h3>
              <p>Calculate entropy using the fundamental formula</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Pie Chart</h3>
              <p>Visualize different types of entropy</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics and thermodynamics students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Entropy?</h3>
              <p className="text-gray-600">
                Entropy is a measure of disorder or randomness in a system. It's a fundamental concept in thermodynamics and statistical mechanics.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Heat transfer should be in joules (J)</li>
                  <li>Temperature should be in kelvin (K)</li>
                  <li>The result will be in joules per kelvin (J/K)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the pie chart work?</h3>
              <p className="text-gray-600">
                The pie chart shows different types of entropy:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Thermal Entropy (60%): Related to heat transfer</li>
                  <li>Chemical Entropy (20%): Related to chemical reactions</li>
                  <li>Configurational Entropy (20%): Related to molecular arrangements</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Entropy calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Thermodynamic systems</li>
                  <li>Chemical reactions</li>
                  <li>Material science</li>
                  <li>Information theory</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Engineers analyze thermodynamic systems</li>
                  <li>Students understand entropy concepts</li>
                  <li>Teachers demonstrate entropy principles</li>
                  <li>Anyone needing to calculate entropy changes</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntropyCalculator;
